import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Navigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth.store';

type Props = {
  title: string;
  subtitle?: string;
  children: ReactNode;
};

const SUB_NAV = [
  { to: '/admin/dashboard',     labelKey: 'admin.nav_dashboard',      icon: '📊' },
  { to: '/admin/users',         labelKey: 'admin.nav_users',         icon: '🧑‍💼' },
  { to: '/admin/organization',  labelKey: 'admin.nav_organization',  icon: '🏢' },
  { to: '/admin/billing',       labelKey: 'admin.nav_billing',       icon: '💳' },
  { to: '/admin/integrations',  labelKey: 'admin.nav_integrations',  icon: '🔌' },
  { to: '/admin/approval-matrix', labelKey: 'admin.nav_approval_matrix', icon: '🗂️' },
  { to: '/admin/folders',       labelKey: 'admin.nav_folders',       icon: '📂' },
  { to: '/admin/audit',         labelKey: 'admin.nav_audit',         icon: '🧾' },
];

/**
 * Admin-only wrapper. Renders a distinct banner + horizontal sub-nav,
 * so it's visually obvious the user is in privileged territory.
 * Non-admin users are redirected to /dashboard.
 */
export function AdminLayout({ title, subtitle, children }: Props) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);

  if (!user || (user.role !== 'owner' && user.role !== 'admin')) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AppShell>
      {/* Admin mode banner */}
      <div
        className="mb-5 rounded-lg px-4 py-3 flex items-center gap-3 border"
        style={{
          background: 'linear-gradient(135deg, rgba(15,42,71,0.92) 0%, rgba(30,58,95,0.92) 100%)',
          color: '#fff',
          borderColor: 'rgba(255,255,255,0.08)',
        }}
      >
        <div className="text-xl">🛡</div>
        <div className="flex-1">
          <div className="text-[11px] uppercase font-semibold tracking-wider text-white/60">
            {t('admin.mode')}
          </div>
          <div className="text-[14px] font-semibold">{t('admin.banner_text')}</div>
        </div>
        <div
          className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded"
          style={{ background: 'rgba(239,68,68,0.18)', color: '#FCA5A5' }}
        >
          {user.role}
        </div>
      </div>

      {/* Sub-nav */}
      <nav className="flex gap-1 mb-5 overflow-x-auto pb-1 border-b border-border">
        {SUB_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-1.5 px-3 py-2 rounded-t-md text-[13px] font-medium no-underline whitespace-nowrap transition-colors',
                isActive
                  ? 'bg-white text-primary border border-border border-b-white -mb-px'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
              )
            }
          >
            <span>{item.icon}</span>
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}
      </nav>

      {/* Page header */}
      <div className="mb-6">
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>

      {children}
    </AppShell>
  );
}
